// src/components/setup/CleanInitialization.jsx
import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiDatabase, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { clearAllCachedData, initializeCleanHealeHub } from '../../utils/clearCachedData';
import { runTenantSetup } from '../../utils/runTenantSetup';
import toast from 'react-hot-toast';

const CleanInitialization = () => {
  const [initStatus, setInitStatus] = useState('idle');
  const [setupStatus, setSetupStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if this is a clean initialization
    const isCleanInit = localStorage.getItem('HEALEHUB_CLEAN_INIT');
    const setupRequired = localStorage.getItem('HEALEHUB_SETUP_REQUIRED');
    
    if (isCleanInit && setupRequired) {
      console.log('🎯 Clean HealeHub initialization detected');
    }
  }, []);

  const handleClearCachedData = () => {
    setInitStatus('running');
    setError(null);
    
    try {
      const success = clearAllCachedData();
      if (success) {
        setInitStatus('success');
        toast.success('All cached data cleared successfully!');
      } else {
        setInitStatus('error');
        setError('Failed to clear some cached data');
      }
    } catch (err) {
      setInitStatus('error');
      setError(err.message);
      toast.error('Failed to clear cached data');
    }
  };

  const handleInitializeClean = () => {
    toast.success('Initializing clean HealeHub...');
    initializeCleanHealeHub();
  };

  const handleSetupMultiTenant = async () => {
    setSetupStatus('running');
    setError(null);
    
    try {
      console.log('🏢 Setting up clean multi-tenant system...');
      
      // Run the tenant setup to create clean multi-tenant structure
      const results = await runTenantSetup();
      
      setSetupStatus('success');
      toast.success('Multi-tenant system initialized successfully!');
      
      // Clear setup flags
      localStorage.removeItem('HEALEHUB_SETUP_REQUIRED');
      
      console.log('✅ Clean multi-tenant setup complete:', results);
      
    } catch (err) {
      console.error('❌ Multi-tenant setup failed:', err);
      setSetupStatus('error');
      setError(err.message);
      toast.error('Multi-tenant setup failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <FiDatabase className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            HealeHub Clean Initialization
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Set up HealeHub as a clean multi-tenant SaaS platform with isolated clinic data.
          </p>
        </div>

        {/* Setup Steps */}
        <div className="space-y-8">
          
          {/* Step 1: Clear Cached Data */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Step 1: Clear Cached Data
                </h2>
                <p className="text-gray-600">
                  Remove all cached client data from browser storage to ensure clean start.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {initStatus === 'success' && <FiCheck className="h-5 w-5 text-green-500" />}
                {initStatus === 'error' && <FiAlertCircle className="h-5 w-5 text-red-500" />}
              </div>
            </div>
            
            <button
              onClick={handleClearCachedData}
              disabled={initStatus === 'running'}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              {initStatus === 'running' ? (
                <FiRefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <FiRefreshCw className="h-4 w-4" />
              )}
              <span>Clear All Cached Data</span>
            </button>
          </div>

          {/* Step 2: Initialize Clean System */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Step 2: Initialize Clean HealeHub
                </h2>
                <p className="text-gray-600">
                  Force reload with clean Firebase connection and prepare for multi-tenant setup.
                </p>
              </div>
            </div>
            
            <button
              onClick={handleInitializeClean}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <FiDatabase className="h-4 w-4" />
              <span>Initialize Clean System</span>
            </button>
          </div>

          {/* Step 3: Setup Multi-Tenant */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Step 3: Setup Multi-Tenant System
                </h2>
                <p className="text-gray-600">
                  Create the clean multi-tenant architecture with isolated clinic data.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {setupStatus === 'success' && <FiCheck className="h-5 w-5 text-green-500" />}
                {setupStatus === 'error' && <FiAlertCircle className="h-5 w-5 text-red-500" />}
              </div>
            </div>
            
            <button
              onClick={handleSetupMultiTenant}
              disabled={setupStatus === 'running'}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              {setupStatus === 'running' ? (
                <FiRefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <FiDatabase className="h-4 w-4" />
              )}
              <span>Setup Multi-Tenant System</span>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <FiAlertCircle className="h-5 w-5 text-red-500" />
                <p className="text-red-700 font-medium">Error:</p>
              </div>
              <p className="text-red-600 mt-1">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {setupStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <FiCheck className="h-8 w-8 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-900 mb-2">
                HealeHub Initialized Successfully!
              </h3>
              <p className="text-green-700">
                Your clean multi-tenant SaaS platform is ready. You can now add clinics with complete data isolation.
              </p>
              <button
                onClick={() => window.location.href = '/'}
                className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleanInitialization;
